// FormLocationUpdate.jsx
import { FiCheck, FiCrosshair, FiLoader, FiPlus, FiMinus, FiInfo } from "react-icons/fi";
import { useEffect, useState } from "react";
import { MapContainer, TileLayer, Marker, useMapEvents, Popup, LayersControl, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { PppoeUserService } from "../../../../../../../services/PppoeUserService";
import { getLocationDetail } from "../../../../../../../utilities/leftjs/getLocationDetail";
import { FullscreenControl } from "../../../../../../../utilities/leftjs/FullScreen";

const { BaseLayer } = LayersControl;

const DEFAULT_CENTER = [-6.17511, 106.827153];

const ChangeView = ({ center, zoom }) => {
  const map = useMap();
  useEffect(() => {
    if (center && center[0] && center[1]) {
      map.flyTo(center, zoom || 16, { duration: 1.2 });
    }
  }, [center, zoom, map]);
  return null;
};

const CtrlScrollZoom = () => {
  const map = useMap();
  useEffect(() => {
    const container = map.getContainer();
    const onWheel = (e) => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault(); e.stopPropagation();
        const steps = Math.abs(e.deltaY) > 150 ? 2 : 1;
        if (e.deltaY < 0) map.zoomIn(steps, { animate: true });
        else if (e.deltaY > 0) map.zoomOut(steps, { animate: true });
      }
    };
    container.addEventListener("wheel", onWheel, { passive: false, capture: true });
    return () => container.removeEventListener("wheel", onWheel, { capture: true });
  }, [map]);
  return null;
};

const ZoomButtons = () => {
  const map = useMap();
  const btnBase = {
    width: 34, height: 34, display: "flex", alignItems: "center", justifyContent: "center",
    background: "rgba(15,23,42,0.9)", backdropFilter: "blur(8px)",
    border: "1px solid rgba(51,65,85,0.5)", color: "#e2e8f0", cursor: "pointer", transition: "all 0.15s ease",
  };
  return (
    <div style={{
      position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)",
      display: "flex", flexDirection: "column", gap: 2, zIndex: 1000,
      borderRadius: 10, overflow: "hidden", boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
    }}>
      <button onClick={() => map.zoomIn(null, { animate: true })} style={{ ...btnBase, borderRadius: "9px 9px 0 0" }} title="Zoom In"
        onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(0,180,216,0.3)"; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(15,23,42,0.9)"; }}>
        <FiPlus size={16} strokeWidth={3} />
      </button>
      <div style={{ height: 1, background: "rgba(51,65,85,0.4)" }} />
      <button onClick={() => map.zoomOut(null, { animate: true })} style={{ ...btnBase, borderRadius: "0 0 9px 9px" }} title="Zoom Out"
        onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(0,180,216,0.3)"; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(15,23,42,0.9)"; }}>
        <FiMinus size={16} strokeWidth={3} />
      </button>
    </div>
  );
};

const ScrollHint = () => {
  const [visible, setVisible] = useState(true);
  useEffect(() => { const t = setTimeout(() => setVisible(false), 4000); return () => clearTimeout(t); }, []);
  if (!visible) return null;
  const kbd = (t) => <kbd style={{ display: "inline-block", padding: "1px 5px", background: "rgba(51,65,85,0.6)", borderRadius: 4, fontSize: 9, fontFamily: "monospace", fontWeight: 700, color: "#e2e8f0" }}>{t}</kbd>;
  return (
    <div style={{
      position: "absolute", bottom: 12, left: "50%", transform: "translateX(-50%)", zIndex: 1000,
      display: "flex", alignItems: "center", gap: 6, padding: "6px 14px",
      background: "rgba(15,23,42,0.88)", backdropFilter: "blur(10px)", borderRadius: 20,
      border: "1px solid rgba(51,65,85,0.4)", boxShadow: "0 4px 16px rgba(0,0,0,0.25)",
      fontSize: 10, fontWeight: 600, color: "#94a3b8", whiteSpace: "nowrap",
      animation: "fadeHint 0.5s ease-out, fadeHintOut 0.5s ease-in 3.5s forwards", pointerEvents: "none",
    }}>
      <FiInfo size={12} style={{ color: "#0ea5e9" }} />
      {kbd("Ctrl")} + {kbd("Scroll")} untuk zoom
    </div>
  );
};

const MapClickHandler = ({ onLocationSelect, disabled }) => {
  useMapEvents({
    click(e) { if (!disabled) onLocationSelect(e.latlng.lat, e.latlng.lng); },
  });
  return null;
};

const selectedIcon = new L.Icon({
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
  iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowSize: [41, 41],
});

const isValidCoord = (val) => {
  if (val === null || val === undefined) return false;
  const str = String(val).trim();
  if (str === "") return false;
  const num = parseFloat(str);
  return !isNaN(num) && isFinite(num);
};

export const FormLocationUpdate = ({ id, data, setRefresh, setOpenUpdate }) => {
  const [formData, setFormData] = useState({
    latitude: "",
    longitude: "",
    address_detail: "",
  });

  const [gpsInput, setGpsInput] = useState("");
  const [mapPosition, setMapPosition] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetchingAddress, setFetchingAddress] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const lat = data?.latitude;
    const lng = data?.longitude;
    const addr = data?.address_detail ?? data?.detail_alamat ?? "";
    setFormData({
      latitude: isValidCoord(lat) ? String(lat) : "",
      longitude: isValidCoord(lng) ? String(lng) : "",
      address_detail: addr,
    });
    if (isValidCoord(lat) && isValidCoord(lng)) {
      const latStr = String(lat).trim();
      const lngStr = String(lng).trim();
      setGpsInput(`${latStr}, ${lngStr}`);
      setMapPosition([parseFloat(latStr), parseFloat(lngStr)]);
    }
  }, [data]);

  useEffect(() => {
    if (!gpsInput || gpsInput.trim() === "") return;
    const parts = gpsInput.split(",");
    if (parts.length !== 2) return;
    const lat = parseFloat(parts[0].trim());
    const lng = parseFloat(parts[1].trim());
    if (!isNaN(lat) && !isNaN(lng) && isFinite(lat) && isFinite(lng)) {
      setMapPosition([lat, lng]);
    }
  }, [gpsInput]);

  const onChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleGpsInputChange = (e) => {
    const value = e.target.value;
    setGpsInput(value);
    const parts = value.split(",");
    if (parts.length === 2) {
      const lat = parseFloat(parts[0].trim());
      const lng = parseFloat(parts[1].trim());
      if (!isNaN(lat) && !isNaN(lng)) {
        onChange("latitude", lat);
        onChange("longitude", lng);
      }
    }
  };

  const applyGpsInput = () => {
    if (!gpsInput.trim()) return;
    const parts = gpsInput.split(",");
    if (parts.length !== 2) return;
    const lat = parseFloat(parts[0].trim());
    const lng = parseFloat(parts[1].trim());
    if (!isNaN(lat) && !isNaN(lng)) {
      onChange("latitude", lat);
      onChange("longitude", lng);
      fetchAddress(lat, lng);
    }
  };

  const fetchAddress = async (lat, lng) => {
    const latStr = typeof lat === "number" ? lat.toFixed(7) : lat;
    const lngStr = typeof lng === "number" ? lng.toFixed(7) : lng;
    setFetchingAddress(true);
    try {
      const locationData = await getLocationDetail(latStr, lngStr);
      if (locationData?.displayName) onChange("address_detail", locationData.displayName);
    } catch (err) {
      console.error("Gagal mengambil alamat:", err);
    } finally {
      setFetchingAddress(false);
    }
  };

  const handleLocationSelect = async (lat, lng) => {
    const latStr = typeof lat === "number" ? lat.toFixed(7) : String(lat).trim();
    const lngStr = typeof lng === "number" ? lng.toFixed(7) : String(lng).trim();
    onChange("latitude", latStr);
    onChange("longitude", lngStr);
    setGpsInput(`${latStr}, ${lngStr}`);
    await fetchAddress(latStr, lngStr);
  };

  const handleDetectGPS = () => {
    if (!navigator.geolocation) { alert("Browser tidak mendukung Geolocation"); return; }
    setLoading(true);
    navigator.geolocation.getCurrentPosition(
      (position) => { handleLocationSelect(position.coords.latitude, position.coords.longitude); setLoading(false); },
      (error) => { console.error(error); alert("Gagal mendapatkan lokasi. Pastikan GPS aktif."); setLoading(false); },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isValidCoord(formData.latitude) || !isValidCoord(formData.longitude)) {
      alert("Koordinat belum tersedia. Pilih titik di peta atau deteksi GPS.");
      return;
    }
    setIsSubmitting(true);
    try {
      await PppoeUserService.locationUpdate({ id, data: formData });
      setRefresh?.(true);
      setOpenUpdate(false);
    } catch (error) {
      console.error(error);
      alert(error.response?.data?.message || "Gagal menyimpan lokasi");
    } finally {
      setIsSubmitting(false);
    }
  };

  const INPUT_BASE = { background: "rgba(15,23,42,0.7)", border: "1px solid rgba(51,65,85,0.4)", color: "#e2e8f0" };
  const INPUT_FOCUS = { borderColor: "rgba(0,180,216,0.5)", boxShadow: "0 0 0 3px rgba(0,180,216,0.08)" };
  const isBusy = loading || fetchingAddress || isSubmitting;
  const hasMarker = mapPosition !== null;

  return (
    <div className="h-full flex flex-col rounded-xl overflow-hidden">
      {/* ==================== PETA — SELALU TAMPIL ==================== */}
      <div className="flex-1 min-h-[240px] relative" style={{ zIndex: 0 }}>
        <MapContainer
          center={mapPosition || DEFAULT_CENTER}
          zoom={mapPosition ? 16 : 13}
          maxZoom={20}
          minZoom={3}
          style={{ height: "100%", width: "100%" }}
          scrollWheelZoom={false}
          className="relative w-full h-full"
        >
          {/* FlyTo hanya saat ada koordinat */}
          {hasMarker && <ChangeView center={mapPosition} zoom={16} />}
          <CtrlScrollZoom />

          <LayersControl position="topright">
            <BaseLayer checked name="Google Maps">
              <TileLayer attribution='&copy; Google Maps' url="https://{s}.google.com/vt/lyrs=m&x={x}&y={y}&z={z}" subdomains={['mt0','mt1','mt2','mt3']} maxZoom={20} maxNativeZoom={20} />
            </BaseLayer>
            <BaseLayer name="Google Satellite">
              <TileLayer attribution='&copy; Google Maps' url="https://{s}.google.com/vt/lyrs=y&x={x}&y={y}&z={z}" subdomains={['mt0','mt1','mt2','mt3']} maxZoom={20} maxNativeZoom={20} />
            </BaseLayer>
            <BaseLayer name="OpenStreetMap">
              <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" maxZoom={20} maxNativeZoom={19} />
            </BaseLayer>
          </LayersControl>

          {/* Marker — HANYA saat ada koordinat */}
          {hasMarker && (
            <Marker position={mapPosition} icon={selectedIcon}>
              <Popup>
                <div className="text-center text-xs">
                  <p className="font-bold">Lokasi Terpilih</p>
                  <p className="text-gray-500 mt-0.5 font-mono">{gpsInput}</p>
                </div>
              </Popup>
            </Marker>
          )}

          {/* Klik peta — SELALU aktif, bahkan tanpa marker */}
          <MapClickHandler onLocationSelect={handleLocationSelect} disabled={isBusy} />

          <ZoomButtons />
          <div className="absolute right-[10px] bottom-[10px]" style={{ zIndex: 1000 }}><FullscreenControl /></div>
          <ScrollHint />
        </MapContainer>

        {/* Overlay fetching address */}
        {fetchingAddress && (
          <div className="absolute top-3 left-1/2 -translate-x-1/2 z-[1000] flex items-center gap-2 px-4 py-2 bg-slate-900/90 backdrop-blur-md rounded-full shadow-lg border border-slate-700">
            <FiLoader className="animate-spin text-cyan-400" size={14} />
            <span className="text-[11px] font-semibold text-slate-200">Mencari alamat...</span>
          </div>
        )}

        {/* Hint kecil saat belum ada marker */}
        {!hasMarker && (
          <div className="absolute top-3 left-1/2 -translate-x-1/2 z-[1000] flex items-center gap-2 px-3 py-1.5 bg-slate-900/80 backdrop-blur-md rounded-full border border-slate-700/60">
            <FiCrosshair className="text-slate-500" size={12} />
            <span className="text-[10px] text-slate-400 font-medium">Klik peta atau deteksi GPS</span>
          </div>
        )}
      </div>

      {/* ==================== FORM ==================== */}
      <form onSubmit={handleSubmit} className="p-3 space-y-2.5 border-t border-slate-700/50 bg-slate-900/80 backdrop-blur-sm">
        <div>
          <label className="text-[8px] font-bold text-slate-500 uppercase tracking-widest block mb-1.5">GPS</label>
          <div className="relative">
            <input
              type="text" inputMode="decimal"
              placeholder="-6.17511, 106.827153"
              value={gpsInput} onChange={handleGpsInputChange}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); applyGpsInput(); } }}
              className="w-full bg-slate-800/50 border border-slate-600/50 rounded-xl px-4 py-2.5 text-xs text-slate-200 placeholder-slate-500 focus:outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/20 transition-all font-mono pr-12"
              style={INPUT_BASE}
              onFocus={(e) => Object.assign(e.currentTarget.style, INPUT_FOCUS)}
              onBlur={(e) => Object.assign(e.currentTarget.style, INPUT_BASE)}
            />
            <button
              type="button" onClick={handleDetectGPS} disabled={isBusy}
              className="absolute right-1.5 top-1/2 -translate-y-1/2 p-2 rounded-lg text-cyan-400 hover:bg-cyan-500/10 transition-all disabled:opacity-50"
              title="Deteksi GPS"
            >
              {loading ? <FiLoader className="animate-spin" size={14} /> : <FiCrosshair size={14} />}
            </button>
          </div>
          <p className="text-[9px] text-slate-600 mt-1">Format: latitude, longitude — tekan Enter untuk apply</p>
        </div>

        <div>
          <label className="text-[8px] font-bold text-slate-500 uppercase tracking-widest block mb-1.5">
            Alamat Lengkap
            {fetchingAddress && <FiLoader className="inline ml-1.5 animate-spin text-cyan-400" size={10} />}
          </label>
          <textarea
            rows={2} value={formData.address_detail}
            onChange={(e) => onChange("address_detail", e.target.value)}
            className="w-full px-3 py-2 rounded-lg text-xs text-slate-200 outline-none resize-none transition-all"
            style={INPUT_BASE}
            onFocus={(e) => Object.assign(e.currentTarget.style, INPUT_FOCUS)}
            onBlur={(e) => Object.assign(e.currentTarget.style, INPUT_BASE)}
            placeholder="Klik peta atau deteksi GPS untuk mendapatkan alamat otomatis..."
          />
        </div>

        <div className="flex gap-2 pt-1">
          <button
            type="button" onClick={() => setOpenUpdate(false)}
            className="flex-1 py-2.5 rounded-lg font-bold text-[11px] text-slate-400 bg-slate-800/60 border border-slate-700 hover:bg-slate-700/60 transition-all"
          >
            Batal
          </button>
          <button
            type="submit" disabled={isBusy || !isValidCoord(formData.latitude) || !isValidCoord(formData.longitude)}
            className="flex-1 py-2.5 rounded-lg font-bold text-[11px] text-white bg-cyan-600 hover:bg-cyan-500 shadow-lg shadow-cyan-600/20 flex items-center justify-center gap-1.5 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {isSubmitting ? <FiLoader className="animate-spin" /> : <FiCheck />}
            {isSubmitting ? "Menyimpan..." : "Simpan Lokasi"}
          </button>
        </div>
      </form>

      <style>{`
        @keyframes fadeHint {
          from { opacity: 0; transform: translateX(-50%) translateY(8px); }
          to   { opacity: 1; transform: translateX(-50%) translateY(0); }
        }
        @keyframes fadeHintOut {
          from { opacity: 1; }
          to   { opacity: 0; pointer-events: none; }
        }
      `}</style>
    </div>
  );
};